import React, { useState } from "react";
import { useBookingContext } from "../context/BookingContext";
import moment from "moment";
import { useTranslation } from "react-i18next";

function ZoneOutsideIsland() {
  const { data, setOpenZoneOutsideIsland, dateUsage, setDateUsage } = useBookingContext();
  const { pickup_date, dropoff_date } = data;
  const { t } = useTranslation();

  const defautValue = dateUsage?.outOfIsland?.map((date) => {
    return date;
  });

  const [initData, setInitData] = useState(defautValue || []);
  const [currentMonth, setCurrentMonth] = useState(moment(pickup_date));

  const startDate = moment(pickup_date, "YYYY-MM-DD");
  const endDate = moment(dropoff_date, "YYYY-MM-DD");

  const datesInRange = [];
  let currentDate = startDate.clone();

  while (currentDate.isBefore(endDate)) {
    datesInRange.push(currentDate.format("YYYY-MM-DD"));
    currentDate.add(1, "days");
  }

  // Generate calendar days
  const generateCalendarDays = () => {
    const startOfMonth = currentMonth.clone().startOf("month");
    const endOfMonth = currentMonth.clone().endOf("month");
    const startDay = startOfMonth.day();

    const days = [];

    // Add empty cells for days before month starts
    for (let i = 0; i < startDay; i++) {
      days.push(null);
    }

    // Add all days of the month
    for (let i = 1; i <= endOfMonth.date(); i++) {
      days.push(currentMonth.clone().date(i));
    }

    return days;
  };

  const calendarDays = generateCalendarDays();
  const weekDays = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  const handleDateToggle = (date) => {
    const dateStr = date.format("YYYY-MM-DD");
    if (!datesInRange.includes(dateStr)) return;

    let items = [...initData];
    const itemExists = items.find((i) => i === dateStr);

    if (!itemExists) {
      items.push(dateStr);
    } else {
      items = items.filter((v) => v !== dateStr);
    }
    setInitData(items);
  };

  const isSelected = (date) => {
    if (!date) return false;
    const dateStr = date.format("YYYY-MM-DD");
    return initData?.find((val) => val === dateStr);
  };

  const isInRange = (date) => {
    if (!date) return false;
    const dateStr = date.format("YYYY-MM-DD");
    return datesInRange.includes(dateStr);
  };

  return (
    <div className="w-full max-w-2xl p-6 bg-white rounded-3xl md:p-8">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <div className="flex items-center justify-center w-10 h-10 shadow-lg bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl">
            <i className="text-white fa-solid fa-plane-departure"></i>
          </div>
          <div>
            <h3 className="text-xl font-bold md:text-2xl text-slate-900">{t("select_travel_date_island")}</h3>
            <p className="text-sm text-slate-600">Outside Island Usage</p>
          </div>
        </div>
      </div>

      {/* Date Range Info */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        <div className="p-4 border-2 border-green-200 bg-gradient-to-br from-green-50 to-white rounded-xl">
          <p className="mb-1 text-xs font-semibold uppercase text-slate-600">Pickup Date</p>
          <p className="text-lg font-bold text-slate-900">{moment(pickup_date).format("MMM D, YYYY")}</p>
        </div>
        <div className="p-4 border-2 border-red-200 bg-gradient-to-br from-red-50 to-white rounded-xl">
          <p className="mb-1 text-xs font-semibold uppercase text-slate-600">Drop-off Date</p>
          <p className="text-lg font-bold text-slate-900">{moment(dropoff_date).format("MMM D, YYYY")}</p>
        </div>
      </div>

      {/* Calendar */}
      <div className="p-5 mb-6 border-2 bg-gradient-to-br from-slate-50 to-white rounded-2xl border-slate-200">
        {/* Month Navigation */}
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={() => setCurrentMonth(currentMonth.clone().subtract(1, "month"))}
            className="flex items-center justify-center w-10 h-10 transition-all duration-300 bg-white border rounded-lg hover:bg-slate-100 border-slate-200"
          >
            <i className="fa-solid fa-chevron-left text-slate-700"></i>
          </button>
          <h4 className="text-lg font-bold text-slate-900">{currentMonth.format("MMMM YYYY")}</h4>
          <button
            onClick={() => setCurrentMonth(currentMonth.clone().add(1, "month"))}
            className="flex items-center justify-center w-10 h-10 transition-all duration-300 bg-white border rounded-lg hover:bg-slate-100 border-slate-200"
          >
            <i className="fa-solid fa-chevron-right text-slate-700"></i>
          </button>
        </div>

        {/* Week Days */}
        <div className="grid grid-cols-7 gap-2 mb-2">
          {weekDays.map((day, idx) => (
            <div key={idx} className="py-2 text-center">
              <span className="text-xs font-semibold text-slate-500">{day}</span>
            </div>
          ))}
        </div>

        {/* Calendar Days */}
        <div className="grid grid-cols-7 gap-2">
          {calendarDays.map((date, idx) => {
            if (!date) {
              return <div key={idx} className="aspect-square"></div>;
            }

            const selected = isSelected(date);
            const inRange = isInRange(date);

            return (
              <button
                key={idx}
                onClick={() => inRange && handleDateToggle(date)}
                disabled={!inRange}
                className={`aspect-square rounded-xl text-sm font-bold transition-all duration-300 ${
                  !inRange
                    ? "bg-slate-100 text-slate-300 cursor-not-allowed"
                    : selected
                    ? "bg-gradient-to-br from-purple-500 to-purple-600 text-white shadow-lg shadow-purple-500/30 scale-105"
                    : "bg-white border-2 border-slate-200 text-slate-700 hover:border-purple-400 hover:bg-purple-50"
                }`}
              >
                {date.format("D")}
              </button>
            );
          })}
        </div>
      </div>

      {/* Summary */}
      <div className="p-4 mb-6 border-2 border-purple-200 bg-gradient-to-r from-purple-50 to-purple-100 rounded-xl">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-10 h-10 bg-white rounded-lg shadow-md">
              <i className="text-lg text-purple-600 fa-solid fa-calendar-check"></i>
            </div>
            <div>
              <p className="text-sm font-semibold text-slate-600">
                {t("total")}: {initData?.length} {t("days")}
              </p>
              <p className="text-xs text-slate-500">Selected for outside island</p>
            </div>
          </div>
          <span className="px-4 py-2 text-sm font-bold text-white bg-purple-500 rounded-xl">{initData?.length}</span>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex flex-col gap-3 sm:flex-row">
        <button
          onClick={() => setOpenZoneOutsideIsland(false)}
          className="flex-1 px-6 py-3 font-semibold transition-all duration-300 border bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl border-slate-200"
        >
          <i className="mr-2 fa-solid fa-xmark"></i>
          {t("close")}
        </button>
        <button
          onClick={() => {
            setDateUsage({ ...dateUsage, outOfIsland: initData });
            setOpenZoneOutsideIsland(false);
          }}
          className="flex-1 px-6 py-3 bg-[#f5272b] hover:bg-red-700 text-white font-semibold rounded-xl transition-all duration-300 shadow-lg shadow-[#f5272b]/30"
        >
          <i className="mr-2 fa-solid fa-check"></i>
          {t("save")}
        </button>
      </div>
    </div>
  );
}

export default ZoneOutsideIsland;
